import { config } from '../config/config';

export type AdminLoginResponse = {
  token: string;
  expiresAtUtc: string;
  username: string;
  isEulaAccepted?: boolean;
  eulaPdfLink?: string | null;
  email?: string | null;
  isAuthenticatorEnrolled?: boolean;
  authenticatorEnrollmentRequired?: boolean;
};

export type EmailExistsResponse = {
  exists: boolean;
  subscriptionEndsAtUtc?: string | null;
};

export type StartupLoginVerificationResponse = {
  exists: boolean;
  hasPhone: boolean;
  phoneLast3?: string | null;
  authenticatorManualKey?: string | null;
  authenticatorOtpAuthUrl?: string | null;
  authenticatorAlreadyEnrolled?: boolean;
  authenticatorSmsRequired?: boolean;
  subscriptionEndsAtUtc?: string | null;
};

export type StartupLoginAuthenticatorSetupResponse = {
  requiresAuthenticatorSetup: boolean;
  authenticatorManualKey?: string | null;
  authenticatorOtpAuthUrl?: string | null;
};

export type ResetAuthenticatorEnrollmentResponse = {
  ok: boolean;
  userId: number;
  username: string;
  email?: string | null;
  wasEnrolled: boolean;
};

export type BeginAuthenticatorEnrollmentResponse = {
  alreadyEnrolled: boolean;
  manualKey?: string | null;
  otpAuthUrl?: string | null;
};

export type ConfirmAuthenticatorEnrollmentResponse = {
  ok: boolean;
  isAuthenticatorEnrolled: boolean;
  enrolledAtUtc?: string | null;
};

export type StartupLoginEulaPdfResponse = {
  fileName: string;
  relativePath: string;
  savedAtUtc: string;
};

export type LastSelectedStockResponse = {
  symbol?: string | null;
  atUtc?: string | null;
};
export type PortfolioGroupByStockResponse = {
  groupByStock: boolean;
};

type PortfolioGroupByStockResponseRaw = {
  groupByStock?: boolean;
  GroupByStock?: boolean;
};

export type PortfolioLastTabResponse = {
  tab: 'holdings' | 'closed';
};

type PortfolioLastTabResponseRaw = {
  tab?: string;
  Tab?: string;
};

export type AdminUserDto = {
  id: number;
  username: string;
  createdAtUtc: string;

  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  verificationceode?: string | null;
  verificationceodeFailedAttempts?: number;
  subscriptionEndsAtUtc?: string | null;
  isActive?: boolean;
  lastLoginAtUtc?: string | null;
  loginCount?: number;
  eulaAcceptedAtUtc?: string | null;
  eulaAcceptedIp?: string | null;
  isEulaAccepted?: boolean;
  eulaPdfLink?: string | null;
  userGroup?: string | null;
  acquisitionChannel?: string | null;
  notes?: string | null;
  isDeleted?: boolean;
  lastSelectedStockSymbol?: string | null;
  lastSelectedStockAtUtc?: string | null;
  portfolioGroupByStock?: boolean;
  portfolioLastTab?: string | null;
};

export type AdminCreateUserRequest = {
  username: string;
  password: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  verificationceode?: string | null;
  userGroup?: string | null;
  acquisitionChannel?: string | null;
  isActive?: boolean;
  subscriptionEndsAtUtc?: string | null;
  eulaAcceptedAtUtc?: string | null;
  notes?: string | null;
};

export type AdminUpdateUserRequest = {
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  verificationceode?: string | null;
  verificationceodeFailedAttempts?: number;
  userGroup?: string | null;
  isActive?: boolean;
  subscriptionEndsAtUtc?: string | null;
  eulaAcceptedAtUtc?: string | null;
  acquisitionChannel?: string | null;
  notes?: string | null;
};

export type AdminResetPasswordRequest = {
  password: string;
};

export type AdminImportUsersExcelFailureDto = {
  rowNumber: number;
  email?: string | null;
  reason: string;
};

export type AdminImportUsersExcelResponse = {
  totalRows: number;
  imported: number;
  skippedExisting: number;
  failed: number;
  failures: AdminImportUsersExcelFailureDto[];
};

export type AdminStockHistoryDto = {
  id: number;
  userId: number;
  username?: string | null;
  stockName: string;
  createdAtUtc: string;
};

export type AdminStockHistoryCreateRequest = {
  userId?: number;
  username?: string;
  category?: string;
  stockName: string;
};

export type AdminStockHistoryDeleteBySymbolResponse = {
  ok: boolean;
  deletedCount: number;
};

export type WatchlistItemDto = {
  id: number;
  symbol: string;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  currentPrice?: number | null;
  changePct?: number | null;
  status: string;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type WatchlistUpsertRequest = {
  symbol: string;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  currentPrice?: number | null;
  changePct?: number | null;
  status?: string | null;
};

export type WatchlistDeleteBySymbolResponse = {
  ok: boolean;
  deletedCount: number;
};

export type WatchlistBulkQuotesRequest = {
  items: Array<{ symbol: string; currentPrice?: number | null; changePct?: number | null }>;
};

export type WatchlistBulkQuotesResponse = {
  ok: boolean;
  updatedCount: number;
};

export type PortfolioHoldingDto = {
  id: number;
  portfolioId: number;
  symbol: string;
  shares: number;
  buyPrice: number;
  currentPrice?: number | null;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  status: string;
  isClosed: boolean;
  buyDateUtc: string;
  sellDateUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type PortfolioHoldingUpsertRequest = {
  id?: number | null;
  portfolioId?: number | null;
  symbol: string;
  shares: number;
  buyPrice: number;
  buyDateUtc?: string | null;
  sellDateUtc?: string | null;
  currentPrice?: number | null;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  status?: string | null;
  isClosed?: boolean | null;
};

export type PortfolioDto = {
  id: number;
  name: string;
  isDefault: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type PortfolioUpsertRequest = {
  id?: number | null;
  name: string;
  isDefault?: boolean | null;
};

export type AdminPortfolioHoldingDto = {
  id: number;
  userId: number;
  username?: string | null;
  portfolioId: number;
  portfolioName?: string | null;
  symbol: string;
  shares: number;
  buyPrice: number;
  currentPrice?: number | null;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  status: string;
  isClosed: boolean;
  buyDateUtc: string;
  sellDateUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type AdminPortfolioHoldingUpsertRequest = {
  id?: number | null;
  userId?: number;
  username?: string;
  category?: string;
  portfolioId?: number | null;
  portfolioName?: string | null;
  symbol: string;
  shares: number;
  buyPrice: number;
  buyDateUtc?: string | null;
  sellDateUtc?: string | null;
  currentPrice?: number | null;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  status?: string | null;
  isClosed?: boolean | null;
};

export type AdminWatchlistItemDto = {
  id: number;
  userId: number;
  username?: string | null;
  symbol: string;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  currentPrice?: number | null;
  changePct?: number | null;
  status: string;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type AdminWatchlistUpsertRequest = {
  userId?: number;
  username?: string;
  category?: string;
  symbol: string;
  buyPriceAlert?: number | null;
  sellPriceAlert?: number | null;
  currentPrice?: number | null;
  changePct?: number | null;
  status?: string | null;
};

export type AdminWatchlistDeleteBySymbolResponse = {
  ok: boolean;
  deletedCount: number;
};

export type AdminWatchlistClearAlertsResponse = {
  ok: boolean;
  updatedCount: number;
};

export type AdminTaskDto = {
  id: string;
  name: string;
  description: string;
  isEnabled: boolean;
  isRunning: boolean;
  lastStartedAtUtc?: string | null;
  lastFinishedAtUtc?: string | null;
  lastResult: string;
  lastError: string;
  lastSymbolsCount: number;
  lastBatchesCount: number;
  lastRowsUpdated: number;
};

export type AdminDashboardStatsDto = {
  userRegistrations: number;
  activeUsers: number;
  bugReports: number;
};

export type AdminNotificationDto = {
  id: number;
  userId: number;
  username?: string | null;
  source: string;
  symbol: string;
  title: string;
  body: string;
  isRead: boolean;
  occurredAtUtc?: string | null;
  createdAtUtc: string;
  readAtUtc?: string | null;
};

export type CreateBugReportRequest = {
  title: string;
  category?: string | null;
  html: string;
  pageUrl?: string | null;
  userAgent?: string | null;
};

export type BugReportDto = {
  id: number;
  userId?: number | null;
  username: string;
  category: string;
  title: string;
  html: string;
  pageUrl?: string | null;
  userAgent?: string | null;
  isResolved: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  resolvedAtUtc?: string | null;
};

export type BugReportsActionResponse = {
  ok: boolean;
  affectedCount: number;
};

export type QuoteCacheDto = {
  symbol: string;
  currentPrice: number | null;
  changePct: number | null;
  updatedAtUtc: string;
};

export type QuotesCacheBatchMeta = {
  phase: string | null;
  ttlSec: number | null;
  staleCount: number | null;
};

export type QuotesCacheBatchResult = {
  rows: QuoteCacheDto[];
  meta: QuotesCacheBatchMeta;
};

export type QuoteCacheUpsertRequest = {
  symbol: string;
  currentPrice: number;
  changePct?: number | null;
};

export type AdminTaskSchedulerUpsertRequest = {
  taskName?: string | null;
  intervalMinutes: number;
  enabled: boolean;
};

export type AdminTaskSchedulerResult = {
  ok: boolean;
  exitCode: number;
  stdOut: string;
  stdErr: string;
};

export type AdminResearchDeskNoteDto = {
  id: number;
  userId: number;
  username?: string | null;
  stockSymbol?: string | null;
  title: string;
  associatedWith?: string | null;
  html: string;
  createdAtUtc: string;
};

export type AdminResearchDeskNoteCreateRequest = {
  userId?: number;
  username?: string;
  category?: string;
  stockSymbol?: string;
  title: string;
  associatedWith?: string | null;
  html: string;
};

export type AdminResearchDeskDeleteNotesRequest = {
  ids: number[];
};

export type AdminResearchDeskDeleteNotesResponse = {
  ok: boolean;
  deletedCount: number;
};

export type AdminResearchDeskScreenshotDto = {
  id: number;
  userId: number;
  username?: string | null;
  sourcePage: string;
  stockSymbol: string;
  title: string;
  createdAtUtc: string;
  thumbDataUrl: string;
};

export type AdminResearchDeskScreenshotCreateRequest = {
  userId?: number;
  username?: string;
  category?: string;
  sourcePage: string;
  stockSymbol: string;
  title: string;
  imageDataUrl: string;
  thumbDataUrl?: string | null;
};

export type AdminResearchDeskDeleteScreenshotsRequest = {
  ids: number[];
};

export type AdminResearchDeskDeleteScreenshotsResponse = {
  ok: boolean;
  deletedCount: number;
};

export type AdminResearchDeskChecklistResponse = {
  userId: number;
  stockSymbol?: string | null;
  checklist: Record<string, boolean>;
  updatedAtUtc?: string | null;
};

export type AdminResearchDeskUpsertChecklistRequest = {
  userId?: number;
  username?: string;
  category?: string;
  stockSymbol?: string;
  checklist: Record<string, boolean>;
};

export type AdminResearchInsightsStateDto = {
  id: number;
  userId: number;
  username?: string | null;
  stockSymbol: string;
  createdAtUtc?: string | null;
  updatedAtUtc?: string | null;

  businessOperationsHtml?: string | null;
  strengthsHtml?: string | null;
  weaknessesHtml?: string | null;
  opportunitiesHtml?: string | null;
  threatsHtml?: string | null;
  moatsHtml?: string | null;
  stockHtml?: string | null;
  otherHtml?: string | null;
};

export type AdminResearchInsightsGetStateResponse = {
  userId: number;
  stockSymbol: string;
  businessOperationsHtml: string;
  strengthsHtml: string;
  weaknessesHtml: string;
  opportunitiesHtml: string;
  threatsHtml: string;
  moatsHtml: string;
  stockHtml: string;
  otherHtml: string;
  updatedAtUtc?: string | null;
};

export type AdminResearchInsightsUpsertStateRequest = {
  userId?: number;
  username?: string;
  category?: string;
  stockSymbol: string;
  businessOperationsHtml: string;
  strengthsHtml: string;
  weaknessesHtml: string;
  opportunitiesHtml: string;
  threatsHtml: string;
  moatsHtml: string;
  stockHtml: string;
  otherHtml: string;
};

export type ResearchInsightsSelfUpsertStateRequest = {
  stockSymbol: string;
  businessOperationsHtml: string;
  strengthsHtml: string;
  weaknessesHtml: string;
  opportunitiesHtml: string;
  threatsHtml: string;
  moatsHtml: string;
  stockHtml: string;
  otherHtml: string;
};

export type AdminResearchInsightsUpsertStateResponse = {
  id: number;
  userId: number;
  stockSymbol: string;
  updatedAtUtc: string;
};

export type AdminResearchInsightsDeleteStatesRequest = {
  ids: number[];
};

export type AdminResearchInsightsDeleteStatesResponse = {
  ok: boolean;
  deletedCount: number;
};

export type ResearchInsightsAttachmentUploadResponse = {
  id: number;
  stockSymbol: string;
  fileName: string;
  contentType: string;
  createdAtUtc: string;
  fileUrl: string;
};

export type AdminResearchChartsStateDto = {
  id: number;
  userId: number;
  username?: string | null;
  stockSymbol: string;
  createdAtUtc?: string | null;
  updatedAtUtc?: string | null;

  startDate?: string | null;
  endDate?: string | null;
  normalizedPE?: string | null;
  normalizedPOCF?: string | null;
  entryMultiple?: string | null;
  exitMultiple?: string | null;
};

export type AdminResearchChartsGetStateResponse = {
  userId: number;
  stockSymbol: string;
  state: unknown;
  updatedAtUtc?: string | null;
};

export type AdminResearchChartsUpsertStateRequest = {
  userId?: number;
  username?: string;
  category?: string;
  stockSymbol: string;
  state: unknown;
};

export type AdminResearchChartsUpsertStateResponse = {
  id: number;
  userId: number;
  stockSymbol: string;
  updatedAtUtc: string;
};

export type AdminResearchChartsDeleteStatesRequest = {
  ids: number[];
};

export type AdminValuationSideInputsDto = {
  base: string;
  growthRate: string;
  multiple: string;
  years: string;
  minimumReturn: string;
};

export type AdminValuationStateDto = {
  id: number;
  userId: number;
  username?: string | null;
  stockSymbol: string;
  epsBase: string;
  epsGrowthRate: string;
  epsMultiple: string;
  epsYears: string;
  epsMinimumReturn: string;
  cashFlowBase: string;
  cashFlowGrowthRate: string;
  cashFlowMultiple: string;
  cashFlowYears: string;
  cashFlowMinimumReturn: string;
  createdAtUtc?: string | null;
  updatedAtUtc?: string | null;
};

export type ValuationStateResponse = {
  userId: number;
  stockSymbol: string;
  eps: AdminValuationSideInputsDto;
  cashFlow: AdminValuationSideInputsDto;
  updatedAtUtc?: string | null;
  createdAtUtc?: string | null;
};

export type ValuationUpsertRequest = {
  stockSymbol: string;
  eps: { base: string; growthRate: string; multiple: string; years: string; minimumReturn: string };
  cashFlow: { base: string; growthRate: string; multiple: string; years: string; minimumReturn: string };
};

export type ValuationDeleteStatesRequest = {
  ids: number[];
};

export type ValuationDeleteStatesResponse = {
  ok: boolean;
  deletedCount: number;
};

export type AdminResearchChartsDeleteStatesResponse = {
  ok: boolean;
  deletedCount: number;
};

export type AdminResearchChartsDeleteMyStateResponse = {
  ok: boolean;
  deletedCount: number;
};

export type MyResearchChartsGetStateResponse = AdminResearchChartsGetStateResponse;

export type MyResearchChartsUpsertStateRequest = {
  stockSymbol: string;
  state: unknown;
};

export type MyResearchChartsUpsertStateResponse = {
  ok: boolean;
  id: number;
  updatedAtUtc?: string | null;
};

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const formatHttpContext = (res: Response): string => {
  const url = (res.url || '').trim();
  const statusText = (res.statusText || '').trim();
  const at = url ? `@ ${url}` : '';
  const st = statusText ? ` ${statusText}` : '';
  return `HTTP ${res.status}${st} ${at}`.trim();
};

const looksLikeHtml = (text: string): boolean => {
  const t = text.trim().slice(0, 400).toLowerCase();
  return t.startsWith('<!doctype html') || t.startsWith('<html') || t.includes('<head') || t.includes('<body');
};

const toJson = async <T>(res: Response): Promise<T> => {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, `Invalid JSON response (${formatHttpContext(res)})`);
  }
};

const readErrorMessage = async (res: Response, fallback: string): Promise<string> => {
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      const parsed = JSON.parse(text) as any;
      const msg = parsed?.message ?? parsed?.Message;
      if (typeof msg === 'string' && msg.trim()) return msg.trim();

      const detail = parsed?.detail ?? parsed?.Detail;
      if (typeof detail === 'string' && detail.trim()) return detail.trim();

      const title = parsed?.title ?? parsed?.Title;
      if (typeof title === 'string' && title.trim()) return title.trim();

      const errors = parsed?.errors ?? parsed?.Errors;
      if (errors && typeof errors === 'object') {
        const firstError = Object.values(errors)
          .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
          .find((entry) => typeof entry === 'string' && entry.trim().length > 0) as string | undefined;
        if (firstError) return firstError.trim();
      }
    } catch {
      // ignore
    }

    if (looksLikeHtml(text)) {
      return `${fallback} (${formatHttpContext(res)}) — received HTML; check IIS /api application mapping or runtime-config backend URL.`;
    }

    return `${fallback} (${formatHttpContext(res)}) — ${text.trim().slice(0, 280)}`;
  }
  return `${fallback} (${formatHttpContext(res)})`;
};

const toSchedulerResult = (raw: any, fallbackOk: boolean): AdminTaskSchedulerResult => {
  const obj = raw ?? {};
  return {
    ok: Boolean(obj.ok ?? obj.Ok ?? fallbackOk),
    exitCode: Number(obj.exitCode ?? obj.ExitCode ?? 0),
    stdOut: String(obj.stdOut ?? obj.StdOut ?? ''),
    stdErr: String(obj.stdErr ?? obj.StdErr ?? obj.message ?? obj.Message ?? ''),
  };
};

const parseChangePctRaw = (raw: unknown): number | null => {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.replace(/%/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const getClientFingerprint = (): string => {
  if (typeof window === 'undefined') return 'server';

  try {
    const key = 'garp-client-fingerprint';
    const existing = window.localStorage.getItem(key);
    if (existing && existing.trim().length > 0) {
      return existing.trim();
    }

    const generated =
      (window.crypto && 'randomUUID' in window.crypto
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
        .replace(/\s+/g, '')
        .slice(0, 128);

    window.localStorage.setItem(key, generated);
    return generated;
  } catch {
    return 'unknown-client';
  }
};

const normalizeHost = (value: string): string => {
  let raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';

  try {
    if (raw.includes('://')) {
      raw = new URL(raw).hostname;
    }
  } catch {
    // Keep parsing with string fallbacks.
  }

  raw = raw
    .replace(/^[a-z]+:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/^www\./, '')
    .replace(/\.+$/, '');

  return raw;
};

const normalizeEmail = (value: string): string => {
  return String(value || '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, '')
    .trim()
    .toLowerCase();
};

  const PRO_PROD_BETA_BYPASS_EMAIL = 'asvi@garp.com';

const extractUsernameFromEmail = (email: string): string => {
  const normalized = normalizeEmail(email);
  if (!normalized.includes('@')) return normalized;
  return normalized.split('@')[0].trim();
};

const isBlockedByProProdWhitelist = (email: string): boolean => {
  if (typeof window === 'undefined') return false;

  const targetHost = normalizeHost(config.proProdAccess?.host || '');
  if (!targetHost) return false;

  const currentHost = normalizeHost(window.location.hostname || '');
  const isMatchedHost = currentHost === targetHost || currentHost.endsWith(`.${targetHost}`);
  if (!isMatchedHost) return false;

  const allowedEmails = new Set<string>();
  const allowedUsernames = new Set<string>();
  (config.proProdAccess?.allowedUsers || [])
    .map((u) => String(u || '').trim().toLowerCase())
    .filter((u) => u.length > 0)
    .forEach((u) => {
      if (u.includes('@')) {
        allowedEmails.add(u);
        return;
      }
      allowedUsernames.add(u);
    });
  if (allowedEmails.size === 0 && allowedUsernames.size === 0) return false;

  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !normalizedEmail.includes('@')) return true;
  if (normalizedEmail === PRO_PROD_BETA_BYPASS_EMAIL) return false;
  if (allowedEmails.has(normalizedEmail)) return false;

  const username = extractUsernameFromEmail(email);
  if (!username) return true;
  return !allowedUsernames.has(username);
};

const redirectToAirWithEmail = (email: string): void => {
  if (typeof window === 'undefined') return;

  const base = String(config.proProdAccess?.redirectUrl || 'https://garpvi.com').trim() || 'https://garpvi.com';
  const normalized = normalizeEmail(email);

  if (!normalized || !normalized.includes('@')) {
    window.location.assign(base);
    return;
  }

  try {
    const target = new URL(base);
    target.searchParams.set('startupEmail', normalized);
    window.location.assign(target.toString());
  } catch {
    const sep = base.includes('?') ? '&' : '?';
    window.location.assign(`${base}${sep}startupEmail=${encodeURIComponent(normalized)}`);
  }
};

export const adminApi = {
  baseUrl(): string {
    return config.backend.baseUrl;
  },

  async listTasks(token: string): Promise<AdminTaskDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading tasks');
    }

    return toJson<AdminTaskDto[]>(res);
  },

  async listNotifications(
    token: string,
    opts?: { skip?: number; take?: number }
  ): Promise<AdminNotificationDto[]> {
    const skip = Number(opts?.skip ?? 0);
    const take = Number(opts?.take ?? 500);
    const qs = new URLSearchParams();
    if (Number.isFinite(skip) && skip > 0) qs.set('skip', String(Math.floor(skip)));
    if (Number.isFinite(take) && take > 0) qs.set('take', String(Math.floor(take)));

    const url = `${config.backend.baseUrl}/admin/notifications${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading notifications');
      throw new ApiError(res.status, msg);
    }

    return toJson<AdminNotificationDto[]>(res);
  },

  async getDashboardStats(token: string): Promise<AdminDashboardStatsDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/dashboard-stats`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading dashboard stats');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res);
    return {
      userRegistrations: Number(raw?.userRegistrations ?? raw?.UserRegistrations ?? 0),
      activeUsers: Number(raw?.activeUsers ?? raw?.ActiveUsers ?? 0),
      bugReports: Number(raw?.bugReports ?? raw?.BugReports ?? 0),
    };
  },

  async createBugReport(token: string, req: CreateBugReportRequest): Promise<BugReportDto> {
    const res = await fetch(`${config.backend.baseUrl}/bug-reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        title: req.title,
        category: req.category ?? null,
        html: req.html,
        pageUrl: req.pageUrl ?? null,
        userAgent: req.userAgent ?? null,
      }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed saving bug report');
      throw new ApiError(res.status, msg);
    }

    return toJson<BugReportDto>(res);
  },

  async listAdminBugReports(
    token: string,
    opts?: { isResolved?: boolean; skip?: number; take?: number }
  ): Promise<BugReportDto[]> {
    const qs = new URLSearchParams();
    if (typeof opts?.isResolved === 'boolean') qs.set('isResolved', String(opts.isResolved));
    const skip = Number(opts?.skip ?? 0);
    const take = Number(opts?.take ?? 0);
    if (Number.isFinite(skip) && skip > 0) {
      qs.set('skip', String(Math.floor(skip)));
    }
    if (Number.isFinite(take) && take > 0) {
      qs.set('take', String(Math.floor(take)));
    }

    const url = `${config.backend.baseUrl}/admin/bug-reports${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading bug reports');
      throw new ApiError(res.status, msg);
    }

    return toJson<BugReportDto[]>(res);
  },

  async setAdminBugReportsResolved(
    token: string,
    ids: number[],
    isResolved: boolean
  ): Promise<BugReportsActionResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/bug-reports/set-resolved`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ids, isResolved }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed updating bug reports');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res);
    return {
      ok: Boolean(raw?.ok ?? raw?.Ok ?? false),
      affectedCount: Number(raw?.affectedCount ?? raw?.AffectedCount ?? 0),
    };
  },

  async deleteAdminBugReports(token: string, ids: number[]): Promise<BugReportsActionResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/bug-reports/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ids }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed deleting bug reports');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res);
    return {
      ok: Boolean(raw?.ok ?? raw?.Ok ?? false),
      affectedCount: Number(raw?.affectedCount ?? raw?.AffectedCount ?? 0),
    };
  },

  async setTaskEnabled(token: string, id: string, enabled: boolean): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/${encodeURIComponent(id)}/enabled`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ enabled }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed updating task auto status');
      throw new ApiError(res.status, msg);
    }
  },

  async runQuotesRefreshTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-refresh/run`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed starting quotes refresh');
      throw new ApiError(res.status, msg);
    }
  },

  async stopQuotesRefreshTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-refresh/stop`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed stopping quotes refresh');
      throw new ApiError(res.status, msg);
    }
  },

  async runQuotesCacheRefreshTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-cache-refresh/run`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed starting quotes cache refresh');
      throw new ApiError(res.status, msg);
    }
  },

  async stopQuotesCacheRefreshTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-cache-refresh/stop`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed stopping quotes cache refresh');
      throw new ApiError(res.status, msg);
    }
  },

  async runAlertsStatusRefreshTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/alerts-status-refresh/run`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed starting alerts status refresh');
      throw new ApiError(res.status, msg);
    }
  },

  async stopAlertsStatusRefreshTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/alerts-status-refresh/stop`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed stopping alerts status refresh');
      throw new ApiError(res.status, msg);
    }
  },

  async runSubscriptionExpiryWarningSmsTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/subscription-expiry-warning-sms/run`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new ApiError(res.status, await readErrorMessage(res, 'Failed starting expiry warning email task'));
  },

  async stopSubscriptionExpiryWarningSmsTask(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/subscription-expiry-warning-sms/stop`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new ApiError(res.status, await readErrorMessage(res, 'Failed stopping expiry warning email task'));
  },

  async upsertQuotesRefreshSchedule(
    token: string,
    request: AdminTaskSchedulerUpsertRequest
  ): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-refresh/scheduler`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async runQuotesRefreshScheduledTask(token: string, taskName?: string | null): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-refresh/scheduler/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ taskName: taskName ?? null }),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async upsertQuotesCacheRefreshSchedule(
    token: string,
    request: AdminTaskSchedulerUpsertRequest
  ): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-cache-refresh/scheduler`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async runQuotesCacheRefreshScheduledTask(token: string, taskName?: string | null): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-cache-refresh/scheduler/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ taskName: taskName ?? null }),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async upsertAlertsStatusRefreshSchedule(
    token: string,
    request: AdminTaskSchedulerUpsertRequest
  ): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/alerts-status-refresh/scheduler`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async runAlertsStatusRefreshScheduledTask(token: string, taskName?: string | null): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/alerts-status-refresh/scheduler/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ taskName: taskName ?? null }),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async stopAlertsStatusRefreshScheduledTask(token: string, taskName?: string | null): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/alerts-status-refresh/scheduler/stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ taskName: taskName ?? null }),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async upsertSubscriptionExpiryWarningSmsSchedule(token: string, request: AdminTaskSchedulerUpsertRequest): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/subscription-expiry-warning-sms/scheduler`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(request),
    });
    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async stopQuotesCacheRefreshScheduledTask(token: string, taskName?: string | null): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-cache-refresh/scheduler/stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ taskName: taskName ?? null }),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async getQuotesCache(token: string, symbols: string[]): Promise<QuoteCacheDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/quotes-cache/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ symbols }),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading quotes cache');
    }

    const raw = (await toJson<any[]>(res)) ?? [];
    return raw.map((x) => {
      const rawChange = x.changePct ?? x.ChangePct ?? x.changesPercentage ?? x.ChangesPercentage ?? x.changePercentage ?? x.ChangePercentage;
      return {
        symbol: String(x.symbol ?? x.Symbol ?? ''),
        currentPrice: x.currentPrice == null && x.CurrentPrice == null ? null : Number(x.currentPrice ?? x.CurrentPrice ?? 0),
        changePct: parseChangePctRaw(rawChange),
        updatedAtUtc: String(x.updatedAtUtc ?? x.UpdatedAtUtc ?? ''),
      };
    }).filter((x) => x.symbol.trim().length > 0);
  },

  async getQuotesCacheWithMeta(token: string, symbols: string[]): Promise<QuotesCacheBatchResult> {
    const res = await fetch(`${config.backend.baseUrl}/quotes-cache/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ symbols }),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading quotes cache');
    }

    const phase = res.headers.get('x-garp-quotes-cache-phase');
    const ttlRaw = res.headers.get('x-garp-quotes-cache-ttl-sec');
    const staleRaw = res.headers.get('x-garp-quotes-cache-stale-count');
    const ttlSec = ttlRaw != null ? Number.parseInt(ttlRaw, 10) : NaN;
    const staleCount = staleRaw != null ? Number.parseInt(staleRaw, 10) : NaN;

    const raw = (await toJson<any[]>(res)) ?? [];
    const rows = raw.map((x) => {
      const rawChange = x.changePct ?? x.ChangePct ?? x.changesPercentage ?? x.ChangesPercentage ?? x.changePercentage ?? x.ChangePercentage;
      return {
        symbol: String(x.symbol ?? x.Symbol ?? ''),
        currentPrice: x.currentPrice == null && x.CurrentPrice == null ? null : Number(x.currentPrice ?? x.CurrentPrice ?? 0),
        changePct: parseChangePctRaw(rawChange),
        updatedAtUtc: String(x.updatedAtUtc ?? x.UpdatedAtUtc ?? ''),
      };
    }).filter((x) => x.symbol.trim().length > 0);

    return {
      rows,
      meta: {
        phase: (phase && phase.trim()) ? phase.trim() : null,
        ttlSec: Number.isFinite(ttlSec) ? ttlSec : null,
        staleCount: Number.isFinite(staleCount) ? staleCount : null,
      },
    };
  },

  async upsertQuotesCache(token: string, req: QuoteCacheUpsertRequest): Promise<QuoteCacheDto> {
    const res = await fetch(`${config.backend.baseUrl}/quotes-cache/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        symbol: req.symbol,
        currentPrice: req.currentPrice,
        changePct: req.changePct ?? null,
      }),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed upserting quotes cache');
    }

    const raw = (await toJson<any>(res)) ?? {};
    const rawChange = raw.changePct ?? raw.ChangePct ?? raw.changesPercentage ?? raw.ChangesPercentage ?? raw.changePercentage ?? raw.ChangePercentage;
    return {
      symbol: String(raw.symbol ?? raw.Symbol ?? ''),
      currentPrice: raw.currentPrice == null && raw.CurrentPrice == null ? null : Number(raw.currentPrice ?? raw.CurrentPrice ?? 0),
      changePct: parseChangePctRaw(rawChange),
      updatedAtUtc: String(raw.updatedAtUtc ?? raw.UpdatedAtUtc ?? ''),
    };
  },

  async stopQuotesRefreshScheduledTask(token: string, taskName?: string | null): Promise<AdminTaskSchedulerResult> {
    const res = await fetch(`${config.backend.baseUrl}/admin/tasks/quotes-refresh/scheduler/stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ taskName: taskName ?? null }),
    });

    const raw = await toJson<any>(res).catch(() => null);
    const result = toSchedulerResult(raw, res.ok);
    if (!res.ok && !result.stdErr && !result.stdOut) {
      result.stdErr = `HTTP ${res.status} ${res.statusText || ''}`.trim();
    }
    result.ok = Boolean(result.ok && res.ok);
    return result;
  },

  async login(username: string, password: string): Promise<AdminLoginResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Login failed');
      throw new ApiError(res.status, msg);
    }

    return toJson<AdminLoginResponse>(res);
  },

  async getCurrentUserProfile(token: string): Promise<AdminUserDto> {
    const res = await fetch(`${config.backend.baseUrl}/auth/profile`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading profile');
      throw new ApiError(res.status, msg);
    }

    return toJson<AdminUserDto>(res);
  },

  async checkEmailExists(email: string): Promise<EmailExistsResponse> {
    const qs = new URLSearchParams({ email: String(email ?? '') });
    const res = await fetch(`${config.backend.baseUrl}/auth/email-exists?${qs.toString()}`, {
      method: 'GET',
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed checking email');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res).catch(() => ({}));
    return {
      exists: Boolean(raw?.exists ?? raw?.Exists),
      subscriptionEndsAtUtc: raw?.subscriptionEndsAtUtc ?? raw?.SubscriptionEndsAtUtc ?? null,
    };
  },

  async createStartupLoginVerificationCode(
    email: string,
    method: 'sms' | 'authenticator' = 'sms'
  ): Promise<StartupLoginVerificationResponse> {
    if (isBlockedByProProdWhitelist(email)) {
      redirectToAirWithEmail(email);
      throw new ApiError(403, 'User is not authorized for this site. Redirecting to air...');
    }

    const fingerprint = getClientFingerprint();
    const res = await fetch(`${config.backend.baseUrl}/auth/startup-login/verification-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Fingerprint': fingerprint,
      },
      body: JSON.stringify({ email: String(email ?? ''), method }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed preparing verification code');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res).catch(() => ({}));
    return {
      exists: Boolean(raw?.exists ?? raw?.Exists),
      hasPhone: Boolean(raw?.hasPhone ?? raw?.HasPhone),
      phoneLast3: raw?.phoneLast3 ?? raw?.PhoneLast3 ?? null,
      authenticatorManualKey: raw?.authenticatorManualKey ?? raw?.AuthenticatorManualKey ?? null,
      authenticatorOtpAuthUrl: raw?.authenticatorOtpAuthUrl ?? raw?.AuthenticatorOtpAuthUrl ?? null,
      authenticatorAlreadyEnrolled: Boolean(raw?.authenticatorAlreadyEnrolled ?? raw?.AuthenticatorAlreadyEnrolled),
      authenticatorSmsRequired: Boolean(raw?.authenticatorSmsRequired ?? raw?.AuthenticatorSmsRequired),
      subscriptionEndsAtUtc: raw?.subscriptionEndsAtUtc ?? raw?.SubscriptionEndsAtUtc ?? null,
    };
  },

  async verifyStartupLoginCode(
    email: string,
    verificationCode: string,
    method: 'sms' | 'authenticator' = 'sms',
    authenticatorSetupStep = false
  ): Promise<AdminLoginResponse & StartupLoginAuthenticatorSetupResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/startup-login/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: String(email ?? ''),
        verificationCode: String(verificationCode ?? ''),
        method,
        authenticatorSetupStep,
      }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Verification code is invalid');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res);
    return {
      ...raw,
      requiresAuthenticatorSetup: Boolean(raw?.requiresAuthenticatorSetup ?? raw?.RequiresAuthenticatorSetup),
      authenticatorManualKey: raw?.authenticatorManualKey ?? raw?.AuthenticatorManualKey ?? null,
      authenticatorOtpAuthUrl: raw?.authenticatorOtpAuthUrl ?? raw?.AuthenticatorOtpAuthUrl ?? null,
    };
  },

  async beginAuthenticatorEnrollment(token: string): Promise<BeginAuthenticatorEnrollmentResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/authenticator/enrollment/begin`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed starting authenticator enrollment');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res).catch(() => ({}));
    return {
      alreadyEnrolled: Boolean(raw?.alreadyEnrolled ?? raw?.AlreadyEnrolled),
      manualKey: raw?.manualKey ?? raw?.ManualKey ?? null,
      otpAuthUrl: raw?.otpAuthUrl ?? raw?.OtpAuthUrl ?? null,
    };
  },

  async confirmAuthenticatorEnrollment(
    token: string,
    verificationCode: string
  ): Promise<ConfirmAuthenticatorEnrollmentResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/authenticator/enrollment/confirm`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ verificationCode: String(verificationCode ?? '') }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed confirming authenticator enrollment');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res).catch(() => ({}));
    return {
      ok: Boolean(raw?.ok ?? raw?.Ok),
      isAuthenticatorEnrolled: Boolean(raw?.isAuthenticatorEnrolled ?? raw?.IsAuthenticatorEnrolled),
      enrolledAtUtc: raw?.enrolledAtUtc ?? raw?.EnrolledAtUtc ?? null,
    };
  },

  async resetAuthenticatorEnrollment(
    token: string,
    params: { email?: string | null; username?: string | null }
  ): Promise<ResetAuthenticatorEnrollmentResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/admin/reset-authenticator-enrollment`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: params?.email ?? null,
        username: params?.username ?? null,
      }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed resetting authenticator enrollment');
      throw new ApiError(res.status, msg);
    }

    const raw = await toJson<any>(res).catch(() => ({}));
    return {
      ok: Boolean(raw?.ok ?? raw?.Ok),
      userId: Number(raw?.userId ?? raw?.UserId ?? 0),
      username: String(raw?.username ?? raw?.Username ?? ''),
      email: raw?.email ?? raw?.Email ?? null,
      wasEnrolled: Boolean(raw?.wasEnrolled ?? raw?.WasEnrolled),
    };
  },

  async createStartupLoginEulaPdf(
    token: string | null | undefined,
    email: string,
    nationalId: string,
    signatureDataUrl: string,
    firstName: string,
    lastName: string,
    eulaText: string,
    consentText: string
  ): Promise<StartupLoginEulaPdfResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const normalizedToken = String(token ?? '').trim();
    if (normalizedToken.length > 0) {
      headers.Authorization = `Bearer ${normalizedToken}`;
    }

    const res = await fetch(`${config.backend.baseUrl}/auth/startup-login/eula-pdf`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email: String(email ?? ''),
        nationalId: String(nationalId ?? ''),
        signatureDataUrl: String(signatureDataUrl ?? ''),
        firstName: String(firstName ?? ''),
        lastName: String(lastName ?? ''),
        eulaText: String(eulaText ?? ''),
        consentText: String(consentText ?? ''),
      }),
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed generating EULA PDF');
      throw new ApiError(res.status, msg);
    }

    return toJson<StartupLoginEulaPdfResponse>(res);
  },

  async resetSubscriptionExpirySms(token: string, userId: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/${userId}/reset-subscription-expiry-sms`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed resetting subscription expiry SMS markers');
      throw new ApiError(res.status, msg);
    }
  },

  async logout(token: string): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/auth/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Best-effort: even if this fails, the client can still clear its token.
    if (!res.ok && res.status !== 401) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Logout failed');
    }
  },

  async refresh(token: string): Promise<AdminLoginResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Session refresh failed');
      throw new ApiError(res.status, msg);
    }

    return toJson<AdminLoginResponse>(res);
  },

  async getLastSelectedStock(token: string): Promise<LastSelectedStockResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/preferences/last-selected`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed to load preferences');
    }

    return toJson<LastSelectedStockResponse>(res);
  },

  async setLastSelectedStock(token: string, symbol: string | null): Promise<LastSelectedStockResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/preferences/last-selected`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ symbol }),
    });
    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed to save preferences');
    }

    return toJson<LastSelectedStockResponse>(res);
  },


  async getPortfolioGroupByStock(token: string): Promise<PortfolioGroupByStockResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/preferences/portfolio-group-by-stock`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed to load preferences');
    }

    const raw = await toJson<PortfolioGroupByStockResponseRaw>(res);
    return { groupByStock: !!(raw.groupByStock ?? raw.GroupByStock) };
  },

  async setPortfolioGroupByStock(token: string, groupByStock: boolean): Promise<PortfolioGroupByStockResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/preferences/portfolio-group-by-stock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ groupByStock }),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed to save preferences');
    }

    const raw = await toJson<PortfolioGroupByStockResponseRaw>(res);
    return { groupByStock: !!(raw.groupByStock ?? raw.GroupByStock) };
  },

  async getPortfolioLastTab(token: string): Promise<PortfolioLastTabResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/preferences/portfolio-last-tab`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed to load preferences');
    }

    const raw = await toJson<PortfolioLastTabResponseRaw>(res);
    const tab = String(raw.tab ?? raw.Tab ?? '').trim().toLowerCase();
    return { tab: tab === 'closed' ? 'closed' : 'holdings' };
  },

  async setPortfolioLastTab(token: string, tab: 'holdings' | 'closed'): Promise<PortfolioLastTabResponse> {
    const res = await fetch(`${config.backend.baseUrl}/auth/preferences/portfolio-last-tab`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tab }),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed to save preferences');
    }

    const raw = await toJson<PortfolioLastTabResponseRaw>(res);
    const normalized = String(raw.tab ?? raw.Tab ?? '').trim().toLowerCase();
    return { tab: normalized === 'closed' ? 'closed' : 'holdings' };
  },

  async listMyWatchlist(token: string): Promise<WatchlistItemDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/watchlist`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading watchlist');
    }

    return toJson<WatchlistItemDto[]>(res);
  },

  async upsertMyWatchlistItem(token: string, request: WatchlistUpsertRequest): Promise<WatchlistItemDto> {
    const res = await fetch(`${config.backend.baseUrl}/watchlist/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving watchlist item');
    }

    return toJson<WatchlistItemDto>(res);
  },

  async listMyPortfolioHoldings(token: string): Promise<PortfolioHoldingDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/portfolio-holdings`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading portfolio holdings');
      throw new ApiError(res.status, msg);
    }

    return toJson<PortfolioHoldingDto[]>(res);
  },

  async listMyPortfolioHoldingsAll(token: string): Promise<PortfolioHoldingDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/portfolio-holdings/all`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading portfolio holdings');
      throw new ApiError(res.status, msg);
    }

    return toJson<PortfolioHoldingDto[]>(res);
  },

  async listMyPortfolioHoldingsByPortfolio(token: string, portfolioId: number): Promise<PortfolioHoldingDto[]> {
    const qs = new URLSearchParams({ portfolioId: String(portfolioId) });
    const res = await fetch(`${config.backend.baseUrl}/portfolio-holdings?${qs.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading portfolio holdings');
      throw new ApiError(res.status, msg);
    }

    return toJson<PortfolioHoldingDto[]>(res);
  },

  async listMyPortfolios(token: string): Promise<PortfolioDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/portfolios`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading portfolios');
    }

    return toJson<PortfolioDto[]>(res);
  },

  async upsertMyPortfolio(token: string, request: PortfolioUpsertRequest): Promise<PortfolioDto> {
    const res = await fetch(`${config.backend.baseUrl}/portfolios/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving portfolio');
    }

    return toJson<PortfolioDto>(res);
  },

  async upsertMyPortfolioHolding(token: string, request: PortfolioHoldingUpsertRequest): Promise<PortfolioHoldingDto> {
    const res = await fetch(`${config.backend.baseUrl}/portfolio-holdings/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving portfolio holding');
    }

    return toJson<PortfolioHoldingDto>(res);
  },

  async deleteMyPortfolioHolding(token: string, id: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/portfolio-holdings/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting portfolio holding');
    }
  },

  async deleteMyWatchlistBySymbol(token: string, symbol: string): Promise<WatchlistDeleteBySymbolResponse> {
    const qs = new URLSearchParams({ symbol });
    const res = await fetch(`${config.backend.baseUrl}/watchlist/by-symbol?${qs.toString()}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting watchlist item');
    }

    return toJson<WatchlistDeleteBySymbolResponse>(res);
  },

  async bulkUpdateMyWatchlistQuotes(token: string, request: WatchlistBulkQuotesRequest): Promise<WatchlistBulkQuotesResponse> {
    const res = await fetch(`${config.backend.baseUrl}/watchlist/quotes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed updating quotes');
    }

    return toJson<WatchlistBulkQuotesResponse>(res);
  },

  async listUsers(token: string): Promise<AdminUserDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new ApiError(res.status, 'Not authenticated (please log in again)');
      }
      if (res.status === 403) {
        throw new ApiError(res.status, 'Not authorized (Admin only)');
      }
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading users');
    }

    return toJson<AdminUserDto[]>(res);
  },

  async listPublicUsernames(token?: string | null): Promise<string[]> {
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(`${config.backend.baseUrl}/admin/users/usernames`, {
      method: 'GET',
      headers,
    });

    if (!res.ok) {
      if (res.status === 403) return [];
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading usernames');
    }

    return toJson<string[]>(res);
  },

  async createUser(token: string, request: AdminCreateUserRequest): Promise<AdminUserDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed creating user');
    }

    return toJson<AdminUserDto>(res);
  },

  async importUsersExcel(token: string, file: File): Promise<AdminImportUsersExcelResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${config.backend.baseUrl}/admin/users/import-excel`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed importing users from Excel');
      throw new ApiError(res.status, msg);
    }

    return toJson<AdminImportUsersExcelResponse>(res);
  },

  async updateUser(token: string, id: number, request: AdminUpdateUserRequest): Promise<AdminUserDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/${id}`, {
      method: 'PUT',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed updating user');
    }

    return toJson<AdminUserDto>(res);
  },

  async deleteUser(token: string, id: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting user');
    }
  },

  async deleteUserHard(token: string, id: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/${id}/hard`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting user');
    }
  },

  async resetUserPassword(token: string, id: number, request: AdminResetPasswordRequest): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/${id}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed resetting password');
    }
  },

  async resetUserEula(token: string, id: number): Promise<AdminUserDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/users/${id}/reset-eula`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed resetting user EULA');
    }

    return toJson<AdminUserDto>(res);
  },

  async listStockHistory(token: string): Promise<AdminStockHistoryDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/admin/stock-history`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading stock history');
    }

    return toJson<AdminStockHistoryDto[]>(res);
  },

  async listMyStockHistory(token: string): Promise<AdminStockHistoryDto[]> {
    const res = await fetch(`${config.backend.baseUrl}/stock-history/my`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading stock history');
    }

    return toJson<AdminStockHistoryDto[]>(res);
  },

  async createStockHistory(token: string, request: AdminStockHistoryCreateRequest): Promise<AdminStockHistoryDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/stock-history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed creating stock history');
    }

    return toJson<AdminStockHistoryDto>(res);
  },

  async deleteStockHistory(token: string, id: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/stock-history/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting stock history');
    }
  },

  async deleteStockHistoryBySymbol(
    token: string,
    user: { userId?: number; username?: string; category?: string },
    stockName: string
  ): Promise<AdminStockHistoryDeleteBySymbolResponse> {
    const qs = new URLSearchParams({ stockName });

    if (typeof user.userId === 'number' && Number.isFinite(user.userId) && user.userId > 0) {
      qs.set('userId', String(user.userId));
    }
    if (user.username) {
      const u = String(user.username).trim();
      if (u) qs.set('username', u);
    }
    if (user.category) {
      const c = String(user.category).trim();
      if (c) qs.set('category', c);
    }

    const res = await fetch(`${config.backend.baseUrl}/admin/stock-history/by-symbol?${qs.toString()}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new ApiError(res.status, 'Not authenticated (please log in again)');
      }
      if (res.status === 403) {
        throw new ApiError(res.status, 'Not authorized (Admin required)');
      }
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting stock history');
    }

    return toJson<AdminStockHistoryDeleteBySymbolResponse>(res);
  },

  async listWatchlists(
    token: string,
    filters?: { userId?: number; username?: string; category?: string }
  ): Promise<AdminWatchlistItemDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }

    const res = await fetch(`${config.backend.baseUrl}/admin/watchlists${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading watchlists');
    }

    return toJson<AdminWatchlistItemDto[]>(res);
  },

  async upsertWatchlistItem(token: string, request: AdminWatchlistUpsertRequest): Promise<AdminWatchlistItemDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/watchlists/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving watchlist item');
    }

    return toJson<AdminWatchlistItemDto>(res);
  },

  async deleteWatchlistItem(token: string, id: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/watchlists/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting watchlist item');
    }
  },

  async deleteWatchlistBySymbol(
    token: string,
    user: { userId?: number; username?: string; category?: string },
    symbol: string
  ): Promise<AdminWatchlistDeleteBySymbolResponse> {
    const qs = new URLSearchParams({ symbol });

    if (typeof user.userId === 'number' && Number.isFinite(user.userId) && user.userId > 0) {
      qs.set('userId', String(user.userId));
    }
    if (user.username) {
      const u = String(user.username).trim();
      if (u) qs.set('username', u);
    }
    if (user.category) {
      const c = String(user.category).trim();
      if (c) qs.set('category', c);
    }

    const res = await fetch(`${config.backend.baseUrl}/admin/watchlists/by-symbol?${qs.toString()}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting watchlist item');
    }

    return toJson<AdminWatchlistDeleteBySymbolResponse>(res);
  },

  async clearWatchlistAlerts(token: string, ids: number[]): Promise<AdminWatchlistClearAlertsResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/watchlists/clear-alerts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ids }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message: string | undefined;
      if (text) {
        try {
          const parsed = JSON.parse(text) as any;
          message = typeof parsed?.message === 'string' ? parsed.message : undefined;
        } catch {
          // ignore
        }
      }
      const fallback = text?.trim() ? text.trim().slice(0, 180) : `HTTP ${res.status}`;
      throw new ApiError(res.status, message || `Failed clearing watchlist alerts (${fallback})`);
    }

    // Backend returns PascalCase (Ok/UpdatedCount) but our frontend uses camelCase.
    const raw = (await toJson<any>(res)) ?? {};
    return {
      ok: Boolean(raw.ok ?? raw.Ok),
      updatedCount: Number(raw.updatedCount ?? raw.UpdatedCount ?? 0),
    };
  },

  async listPortfolioHoldings(
    token: string,
    filters?: { userId?: number; username?: string; category?: string; symbol?: string }
  ): Promise<AdminPortfolioHoldingDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }
    if (filters?.symbol) {
      const s = String(filters.symbol).trim();
      if (s) qs.set('symbol', s);
    }

    const res = await fetch(
      `${config.backend.baseUrl}/admin/portfolio-holdings${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const msg = await readErrorMessage(res, 'Failed loading portfolio holdings');
      throw new ApiError(res.status, msg);
    }

    return toJson<AdminPortfolioHoldingDto[]>(res);
  },

  async upsertPortfolioHolding(token: string, request: AdminPortfolioHoldingUpsertRequest): Promise<AdminPortfolioHoldingDto> {
    const res = await fetch(`${config.backend.baseUrl}/admin/portfolio-holdings/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving portfolio holding');
    }

    return toJson<AdminPortfolioHoldingDto>(res);
  },

  async deletePortfolioHolding(token: string, id: number): Promise<void> {
    const res = await fetch(`${config.backend.baseUrl}/admin/portfolio-holdings/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting portfolio holding');
    }
  },

  async listResearchDeskNotes(
    token: string,
    filters?: { userId?: number; username?: string; usernames?: string[]; category?: string; stockSymbol?: string }
  ): Promise<AdminResearchDeskNoteDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }
    for (const u of filters?.usernames ?? []) {
      const nu = String(u).trim();
      if (nu) qs.append('usernames', nu);
    }
    if (filters?.stockSymbol) {
      const s = String(filters.stockSymbol).trim();
      if (s) qs.set('stockSymbol', s);
    }

    const res = await fetch(
      `${config.backend.baseUrl}/research-desk/notes${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research desk notes');
    }

    return toJson<AdminResearchDeskNoteDto[]>(res);
  },

  async listAdminResearchDeskNotes(
    token: string,
    filters?: { userId?: number; username?: string; usernames?: string[]; category?: string; stockSymbol?: string }
  ): Promise<AdminResearchDeskNoteDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }
    for (const u of filters?.usernames ?? []) {
      const nu = String(u).trim();
      if (nu) qs.append('usernames', nu);
    }
    if (filters?.stockSymbol) {
      const s = String(filters.stockSymbol).trim();
      if (s) qs.set('stockSymbol', s);
    }

    const res = await fetch(
      `${config.backend.baseUrl}/admin/research-desk/notes${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research desk notes');
    }

    return toJson<AdminResearchDeskNoteDto[]>(res);
  },

  async createResearchDeskNote(token: string, request: AdminResearchDeskNoteCreateRequest): Promise<AdminResearchDeskNoteDto> {
    const res = await fetch(`${config.backend.baseUrl}/research-desk/notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed creating research desk note');
    }

    return toJson<AdminResearchDeskNoteDto>(res);
  },

  async listResearchDeskScreenshots(
    token: string,
    filters?: {
      userId?: number;
      username?: string;
      category?: string;
      sourcePage?: string;
      stockSymbol?: string;
      title?: string;
      fromDate?: string;
      toDate?: string;
      limit?: number;
    }
  ): Promise<AdminResearchDeskScreenshotDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }
    if (filters?.sourcePage) {
      const sp = String(filters.sourcePage).trim();
      if (sp) qs.set('sourcePage', sp);
    }
    if (filters?.stockSymbol) {
      const s = String(filters.stockSymbol).trim();
      if (s) qs.set('stockSymbol', s);
    }
    if (filters?.title) {
      const t = String(filters.title).trim();
      if (t) qs.set('title', t);
    }
    if (filters?.fromDate) {
      const d = String(filters.fromDate).trim();
      if (d) qs.set('fromDate', d);
    }
    if (filters?.toDate) {
      const d = String(filters.toDate).trim();
      if (d) qs.set('toDate', d);
    }
    if (typeof filters?.limit === 'number' && Number.isFinite(filters.limit) && filters.limit > 0) {
      qs.set('limit', String(Math.floor(filters.limit)));
    }

    const res = await fetch(
      `${config.backend.baseUrl}/research-desk/screenshots${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research desk screenshots');
    }

    return toJson<AdminResearchDeskScreenshotDto[]>(res);
  },

  async createResearchDeskScreenshot(
    token: string,
    request: AdminResearchDeskScreenshotCreateRequest
  ): Promise<AdminResearchDeskScreenshotDto> {
    const res = await fetch(`${config.backend.baseUrl}/research-desk/screenshots`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed creating research desk screenshot');
    }

    return toJson<AdminResearchDeskScreenshotDto>(res);
  },

  async deleteResearchDeskScreenshots(
    token: string,
    request: AdminResearchDeskDeleteScreenshotsRequest
  ): Promise<AdminResearchDeskDeleteScreenshotsResponse> {
    const res = await fetch(`${config.backend.baseUrl}/research-desk/screenshots`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting research desk screenshots');
    }

    return toJson<AdminResearchDeskDeleteScreenshotsResponse>(res);
  },

  async getResearchDeskScreenshotImage(token: string, id: number): Promise<Blob> {
    const res = await fetch(`${config.backend.baseUrl}/research-desk/screenshots/${id}/image`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research desk screenshot image');
    }

    return res.blob();
  },

  async deleteResearchDeskNotes(
    token: string,
    request: AdminResearchDeskDeleteNotesRequest
  ): Promise<AdminResearchDeskDeleteNotesResponse> {
    const res = await fetch(`${config.backend.baseUrl}/research-desk/notes`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting research desk notes');
    }

    return toJson<AdminResearchDeskDeleteNotesResponse>(res);
  },

  async deleteAdminResearchDeskNotes(
    token: string,
    request: AdminResearchDeskDeleteNotesRequest
  ): Promise<AdminResearchDeskDeleteNotesResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/research-desk/notes`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting research desk notes');
    }

    return toJson<AdminResearchDeskDeleteNotesResponse>(res);
  },

  async getResearchDeskChecklist(
    token: string,
    user: { userId?: number; username?: string; category?: string; stockSymbol?: string }
  ): Promise<AdminResearchDeskChecklistResponse> {
    const qs = new URLSearchParams();
    if (typeof user.userId === 'number' && Number.isFinite(user.userId) && user.userId > 0) {
      qs.set('userId', String(user.userId));
    }
    if (user.username) {
      const u = String(user.username).trim();
      if (u) qs.set('username', u);
    }
    if (user.category) {
      const c = String(user.category).trim();
      if (c) qs.set('category', c);
    }
    if (user.stockSymbol) {
      const s = String(user.stockSymbol).trim();
      if (s) qs.set('stockSymbol', s);
    }
    const res = await fetch(`${config.backend.baseUrl}/research-desk/checklist?${qs.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research desk checklist');
    }

    return toJson<AdminResearchDeskChecklistResponse>(res);
  },

  async upsertResearchDeskChecklist(
    token: string,
    request: AdminResearchDeskUpsertChecklistRequest
  ): Promise<AdminResearchDeskChecklistResponse> {
    const res = await fetch(`${config.backend.baseUrl}/research-desk/checklist`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving research desk checklist');
    }

    return toJson<AdminResearchDeskChecklistResponse>(res);
  },

  async listResearchInsightsStates(
    token: string,
    filters?: { userId?: number; username?: string; usernames?: string[]; category?: string; stockSymbol?: string }
  ): Promise<AdminResearchInsightsStateDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }
    for (const u of filters?.usernames ?? []) {
      const nu = String(u).trim();
      if (nu) qs.append('usernames', nu);
    }
    if (filters?.stockSymbol) {
      const sym = String(filters.stockSymbol).trim();
      if (sym) qs.set('stockSymbol', sym);
    }

    const res = await fetch(
      `${config.backend.baseUrl}/admin/research-insights/states${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research insights states');
    }

    return toJson<AdminResearchInsightsStateDto[]>(res);
  },

  async getResearchInsightsState(
    token: string,
    userId: number,
    stockSymbol: string
  ): Promise<AdminResearchInsightsGetStateResponse> {
    const qs = new URLSearchParams({ userId: String(userId), stockSymbol: String(stockSymbol) });
    const res = await fetch(`${config.backend.baseUrl}/admin/research-insights/state?${qs.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research insights state');
    }

    return toJson<AdminResearchInsightsGetStateResponse>(res);
  },

  async upsertResearchInsightsState(
    token: string,
    request: AdminResearchInsightsUpsertStateRequest
  ): Promise<AdminResearchInsightsUpsertStateResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/research-insights/state`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving research insights state');
    }

    return toJson<AdminResearchInsightsUpsertStateResponse>(res);
  },

  async getMyResearchInsightsState(
    token: string,
    stockSymbol: string
  ): Promise<AdminResearchInsightsGetStateResponse> {
    const qs = new URLSearchParams({ stockSymbol: String(stockSymbol) });
    const res = await fetch(`${config.backend.baseUrl}/research-insights/state?${qs.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research insights state');
    }

    return toJson<AdminResearchInsightsGetStateResponse>(res);
  },

  async upsertMyResearchInsightsState(
    token: string,
    request: ResearchInsightsSelfUpsertStateRequest
  ): Promise<AdminResearchInsightsUpsertStateResponse> {
    const res = await fetch(`${config.backend.baseUrl}/research-insights/state`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving research insights state');
    }

    return toJson<AdminResearchInsightsUpsertStateResponse>(res);
  },

  async deleteResearchInsightsStates(
    token: string,
    request: AdminResearchInsightsDeleteStatesRequest
  ): Promise<AdminResearchInsightsDeleteStatesResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/research-insights/states`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting research insights states');
    }

    return toJson<AdminResearchInsightsDeleteStatesResponse>(res);
  },

  async uploadMyResearchInsightsAttachment(
    token: string,
    stockSymbol: string,
    file: File
  ): Promise<ResearchInsightsAttachmentUploadResponse> {
    const formData = new FormData();
    formData.set('stockSymbol', String(stockSymbol));
    formData.set('file', file);

    const res = await fetch(`${config.backend.baseUrl}/research-insights/attachments`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed uploading research insights attachment');
    }

    return toJson<ResearchInsightsAttachmentUploadResponse>(res);
  },

  async getMyResearchInsightsAttachmentFile(token: string, id: number): Promise<Blob> {
    const res = await fetch(`${config.backend.baseUrl}/research-insights/attachments/${id}/file`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research insights attachment file');
    }

    return res.blob();
  },

  async listResearchChartsStates(
    token: string,
    filters?: { userId?: number; userIds?: number[]; username?: string; usernames?: string[]; category?: string; stockSymbol?: string }
  ): Promise<AdminResearchChartsStateDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && Number.isFinite(filters.userId) && filters.userId > 0) {
      qs.set('userId', String(filters.userId));
    }
    for (const id of filters?.userIds ?? []) {
      if (typeof id === 'number' && Number.isFinite(id) && id > 0) {
        qs.append('userIds', String(id));
      }
    }
    if (filters?.username) {
      const u = String(filters.username).trim();
      if (u) qs.set('username', u);
    }
    if (filters?.category) {
      const c = String(filters.category).trim();
      if (c) qs.set('category', c);
    }
    for (const u of filters?.usernames ?? []) {
      const nu = String(u).trim();
      if (nu) qs.append('usernames', nu);
    }
    if (filters?.stockSymbol) {
      const sym = String(filters.stockSymbol).trim();
      if (sym) qs.set('stockSymbol', sym);
    }

    const res = await fetch(
      `${config.backend.baseUrl}/admin/research-charts/states${qs.toString() ? `?${qs.toString()}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research charts states');
    }

    return toJson<AdminResearchChartsStateDto[]>(res);
  },

  async getResearchChartsState(
    token: string,
    user: { userId?: number; username?: string; category?: string },
    stockSymbol: string
  ): Promise<AdminResearchChartsGetStateResponse> {
    const qs = new URLSearchParams({ stockSymbol: String(stockSymbol) });
    if (typeof user.userId === 'number' && Number.isFinite(user.userId) && user.userId > 0) {
      qs.set('userId', String(user.userId));
    } else {
      const u = (user.username || '').trim();
      if (u) qs.set('username', u);
      const c = (user.category || '').trim();
      if (c) qs.set('category', c);
    }
    const res = await fetch(`${config.backend.baseUrl}/admin/research-charts/state?${qs.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research charts state');
    }

    return toJson<AdminResearchChartsGetStateResponse>(res);
  },

  async upsertResearchChartsState(
    token: string,
    request: AdminResearchChartsUpsertStateRequest
  ): Promise<AdminResearchChartsUpsertStateResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/research-charts/state`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving research charts state');
    }

    return toJson<AdminResearchChartsUpsertStateResponse>(res);
  },

  async deleteResearchChartsStates(
    token: string,
    request: AdminResearchChartsDeleteStatesRequest
  ): Promise<AdminResearchChartsDeleteStatesResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/research-charts/states`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting research charts states');
    }

    return toJson<AdminResearchChartsDeleteStatesResponse>(res);
  },

  async deleteMyResearchChartsState(
    token: string,
    stockSymbol: string
  ): Promise<AdminResearchChartsDeleteMyStateResponse> {
    const qs = new URLSearchParams({ stockSymbol: String(stockSymbol) });
    const res = await fetch(`${config.backend.baseUrl}/research-charts/my/state?${qs.toString()}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new ApiError(res.status, 'Not authenticated (please log in again)');
      }
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed deleting research charts state');
    }

    return toJson<AdminResearchChartsDeleteMyStateResponse>(res);
  },

  async getMyResearchChartsState(
    token: string,
    stockSymbol: string
  ): Promise<MyResearchChartsGetStateResponse> {
    const qs = new URLSearchParams({ stockSymbol: String(stockSymbol) });
    const res = await fetch(`${config.backend.baseUrl}/research-charts/my/state?${qs.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed loading research charts state');
    }

    return toJson<MyResearchChartsGetStateResponse>(res);
  },

  async upsertMyResearchChartsState(
    token: string,
    request: MyResearchChartsUpsertStateRequest
  ): Promise<MyResearchChartsUpsertStateResponse> {
    const res = await fetch(`${config.backend.baseUrl}/research-charts/my/state`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const maybe = await toJson<{ message?: string }>(res).catch(() => null);
      throw new ApiError(res.status, maybe?.message || 'Failed saving research charts state');
    }

    return toJson<MyResearchChartsUpsertStateResponse>(res);
  },

  // ── Valuation ──────────────────────────────────────────────────────

  async listValuationStates(
    token: string,
    filters?: { userId?: number; username?: string; stockSymbol?: string }
  ): Promise<AdminValuationStateDto[]> {
    const qs = new URLSearchParams();
    if (typeof filters?.userId === 'number' && filters.userId > 0) qs.set('userId', String(filters.userId));
    if (filters?.username) { const u = String(filters.username).trim(); if (u) qs.set('username', u); }
    if (filters?.stockSymbol) { const s = String(filters.stockSymbol).trim(); if (s) qs.set('stockSymbol', s); }
    const res = await fetch(
      `${config.backend.baseUrl}/admin/valuation/states${qs.toString() ? `?${qs.toString()}` : ''}`,
      { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) { const m = await toJson<{ message?: string }>(res).catch(() => null); throw new ApiError(res.status, m?.message || 'Failed loading valuation states'); }
    return toJson<AdminValuationStateDto[]>(res);
  },

  async deleteValuationStates(
    token: string,
    request: ValuationDeleteStatesRequest
  ): Promise<ValuationDeleteStatesResponse> {
    const res = await fetch(`${config.backend.baseUrl}/admin/valuation/states`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(request),
    });
    if (!res.ok) { const m = await toJson<{ message?: string }>(res).catch(() => null); throw new ApiError(res.status, m?.message || 'Failed deleting valuation states'); }
    return toJson<ValuationDeleteStatesResponse>(res);
  },

  async getMyValuationState(
    token: string,
    stockSymbol: string
  ): Promise<ValuationStateResponse> {
    const qs = new URLSearchParams({ stockSymbol: String(stockSymbol) });
    const res = await fetch(`${config.backend.baseUrl}/valuation/state?${qs.toString()}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { const m = await toJson<{ message?: string }>(res).catch(() => null); throw new ApiError(res.status, m?.message || 'Failed loading valuation state'); }
    return toJson<ValuationStateResponse>(res);
  },

  async upsertMyValuationState(
    token: string,
    request: ValuationUpsertRequest
  ): Promise<{ id: number; userId: number; stockSymbol: string; updatedAtUtc: string }> {
    const res = await fetch(`${config.backend.baseUrl}/valuation/state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(request),
    });
    if (!res.ok) { const m = await toJson<{ message?: string }>(res).catch(() => null); throw new ApiError(res.status, m?.message || 'Failed saving valuation state'); }
    return toJson(res);
  },

  async deleteMyValuationState(
    token: string,
    stockSymbol: string
  ): Promise<{ ok: boolean }> {
    const qs = new URLSearchParams({ stockSymbol: String(stockSymbol) });
    const res = await fetch(`${config.backend.baseUrl}/valuation/state?${qs.toString()}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { const m = await toJson<{ message?: string }>(res).catch(() => null); throw new ApiError(res.status, m?.message || 'Failed deleting valuation state'); }
    return toJson(res);
  },
};

export const isUnauthorized = (err: unknown): boolean => {
  return err instanceof ApiError && err.status === 401;
};

export const isSessionReplacedUnauthorized = (err: unknown): boolean => {
  if (!(err instanceof ApiError) || err.status !== 401) return false;
  const msg = (err.message || '').toLowerCase();
  return msg.includes('another ip/device')
    || msg.includes('another ip')
    || msg.includes('another device')
    || msg.includes('already logged in from another ip');
};
